import React from 'react';
import { Shield, Eye, Database, Lock, Users, Cookie, Mail, FileText, Bot } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO, { PAGE_SEO } from './SEO';

const Privacy: React.FC = () => {
  const lastUpdated = 'August 25, 2026';

  const sections = [
    {
      icon: Database,
      title: 'Information We Collect',
      content: `ClubIQ collects personal data that you give us and data created when you use the app.

• **Account information**: name and email when you create an account.
• **Golf profile data**: handicap, swing characteristics, bag, club distances, and related fitting answers.
• **Photos you submit**: if you photograph a club for identification, that image is sent to OpenAI for that request. ClubIQ does not keep an object-storage copy. ClubIQ has no public storage buckets.
• **Usage events**: first-party product events such as onboarding, bag updates, and feature use.
• **Attribution**: first-touch source, campaign, partner code, or referral code if you arrive with those links.
• **Messages you send us**: contact-form contents so we can reply.

We collect this data to run ClubIQ, personalize club and distance tools, and keep the product working.`,
    },
    {
      icon: Bot,
      title: 'How We Use Artificial Intelligence',
      content: `ClubIQ uses AI to calculate distances, recommend clubs, identify clubs from photos, and answer golf questions in Ask ClubIQ.

• Prompts, your golf profile context, and optional club photos are sent to **OpenAI** to generate a response.
• ClubIQ is a golf assistant. It is not a crisis or medical service. If you write about self-harm, ClubIQ will not continue that conversation and will point you to 988 in the United States.
• AI output is informational. It is not a guarantee of on-course results.`,
    },
    {
      icon: Users,
      title: 'Third Parties That Receive Data',
      content: `We do not sell your personal information. These processors receive data only to operate ClubIQ:

• **Supabase**: account login, database, and row-level security for your profile.
• **Vercel**: website and API hosting.
• **OpenAI**: AI answers and optional club-photo identification.
• **WeatherAPI**: only if a weather key is configured and you use weather features. It receives a location query, not your full golf profile.
• **Email send providers**: only if ClubIQ later sends partner or account email from a ClubIQ-owned mailbox. Unsubscribe requests are stored as a hash, not a raw marketing list.

Google Analytics is not enabled. The old placeholder tracking snippet has been removed. Sentry error monitoring is not enabled. Stripe is not live, so ClubIQ does not collect card numbers.

If we add a new processor, we will name it here before it goes live.`,
    },
    {
      icon: Eye,
      title: 'How We Use Your Information',
      content: `We use the information we collect to:

• Provide, maintain, and improve ClubIQ
• Calculate distances and personalize recommendations
• Answer support and privacy requests
• Measure activation and product usage with first-party events
• Send required account messages

We do not sell your personal information to third parties.`,
    },
    {
      icon: Lock,
      title: 'How We Protect Your Data',
      content: `• Data is encrypted in transit using TLS
• Profiles are stored in Supabase with row-level security
• Object storage is not used, and no ClubIQ bucket is public
• Access to production data is limited to operating the service

No system is perfectly secure. We work to protect your information and to fix issues we find.`,
    },
    {
      icon: Cookie,
      title: 'Cookies and Tracking',
      content: `ClubIQ uses essential cookies and local storage to keep you signed in, remember first-touch links, and remember that you saw the data-collection notice.

We do not use advertising cookies. We do not run Google Analytics. You can clear cookies and local storage in your browser.`,
    },
    {
      icon: FileText,
      title: 'Your Rights, Deletion, and Uploads',
      content: `You can view and update your golf profile in the app.

• **Delete**: open Profile and use Delete my account. That removes your login, profile, bag, distances, and first-party events.
• **Photos**: club photos are not stored in a ClubIQ bucket after identification.
• **Contact messages**: we may keep a contact-form message long enough to reply.
• **Export**: use the contact form if you want a copy of your data.

Deletion is available in the app. You do not have to email us first.`,
    },
    {
      icon: Shield,
      title: "Children's Privacy",
      content: `ClubIQ is not intended for children under 13. We do not knowingly collect personal information from children under 13. If you believe we have, contact us so we can delete it.`,
    },
    {
      icon: Mail,
      title: 'Contact Us About Privacy',
      content: `Questions about this policy can go through our contact form.

**Asgard Technologies, LLC**
Georgia, USA

We aim to respond to privacy requests within 30 days.`,
    },
  ];

  return (
    <>
      <SEO {...PAGE_SEO.privacy} url="/privacy" />
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        <div className="bg-gradient-to-br from-green-600 to-emerald-700 py-16 md:py-24">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl mb-6">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Privacy Policy</h1>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              We collect golf profile data to run ClubIQ. Here is who receives it and how to delete it.
            </p>
            <p className="text-white/70 mt-4 text-sm">Last updated: {lastUpdated}</p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-6 py-16">
          <div className="bg-green-50 border border-green-200 rounded-2xl p-6 md:p-8 mb-12">
            <h2 className="text-xl font-bold text-green-800 mb-4">The Short Version</h2>
            <ul className="space-y-3 text-green-700">
              <li>We collect account and golf profile data, plus optional club photos for AI identification.</li>
              <li>OpenAI, Supabase, and Vercel process data to run the app. We do not sell your data.</li>
              <li>You can delete your account in Profile. There is no public storage bucket.</li>
              <li>
                Read the <Link to="/terms" className="underline font-semibold">Terms of Service</Link> for billing and cancellation.
              </li>
            </ul>
          </div>

          <div className="space-y-12">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <section key={section.title} className="scroll-mt-24" id={section.title.toLowerCase().replace(/\s+/g, '-')}>
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-gray-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 pt-2">{section.title}</h2>
                  </div>
                  <div className="pl-16">
                    <div
                      className="prose prose-gray max-w-none text-gray-600 leading-relaxed"
                      dangerouslySetInnerHTML={{
                        __html: section.content
                          .replace(/\*\*(.*?)\*\*/g, '<strong class="text-gray-900">$1</strong>')
                          .replace(/\n\n/g, '</p><p class="mt-4">')
                          .replace(/\n• /g, '</p><p class="mt-2 pl-4">• '),
                      }}
                    />
                  </div>
                </section>
              );
            })}
          </div>

          <div className="mt-16 pt-8 border-t border-gray-200">
            <p className="text-gray-500 text-sm text-center">
              This Privacy Policy is effective as of {lastUpdated}. Material changes will be posted on this page.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Privacy;
